import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Printer, Plus, Trash, PencilSimple, X, Check } from "@phosphor-icons/react";

const money = (n) => `$${Math.abs(Number(n) || 0).toFixed(2)}`;
const fmtD = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const STATUS = {
  paid: { label: "✓ PAID", cls: "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]" },
  received: { label: "✓ RECEIVED", cls: "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]" },
  verified: { label: "✓ VERIFIED", cls: "border-primary/40 bg-primary/10 text-primary" },
  prepay: { label: "⏳ PREPAY", cls: "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]" },
};

const emptyLine = { date: "", caregiver: "", time: "", hours: "", charge: "", overnight: false, holiday: false };

export default function PrepayLedger({ clientId, onClose }) {
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [pay, setPay] = useState({ amount: "", due_date: "", services_start: "", services_end: "", note: "", autofill: true });
  const [lineFor, setLineFor] = useState(null);
  const [line, setLine] = useState({ ...emptyLine });
  const [editLine, setEditLine] = useState(null);
  const [billDetail, setBillDetail] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/billing/prepay/${clientId}`);
      setData(data);
      const last = (data.payments || [])[data.payments.length - 1];
      const carry = data.current_carry || 0;
      const amt = Math.max(0, Math.round(((data.prepay.amount || 0) + carry) * 100) / 100);
      let start = new Date();
      if (last?.services_end) { start = new Date(last.services_end + "T12:00:00"); start.setDate(start.getDate() + 1); }
      const end = new Date(start); end.setDate(end.getDate() + 6);
      let due = new Date(start);
      if (data.prepay.day !== null && data.prepay.day !== undefined) {
        const target = (data.prepay.day + 1) % 7; // backend weekday 0=Mon → JS 1=Mon
        while (due.getDay() !== target) due.setDate(due.getDate() + 1);
        if (last?.due_date && due <= new Date(last.due_date + "T12:00:00")) due.setDate(due.getDate() + 7);
      }
      const iso = (d) => d.toISOString().slice(0, 10);
      setPay((p) => ({ ...p, amount: p.amount || String(amt || ""), due_date: p.due_date || iso(due),
        services_start: p.services_start || iso(start), services_end: p.services_end || iso(end) }));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line

  const addPayment = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/billing/prepay/${clientId}/payments`, { ...pay, amount: Number(pay.amount) });
      toast.success(`Payment #${data.number} added — ${data.lines} projected shift lines from the schedule`);
      setAddOpen(false);
      setPay({ amount: "", due_date: "", services_start: "", services_end: "", note: "", autofill: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const markReceived = async (p) => {
    try {
      await api.put(`/billing/prepay/payments/${p.id}`, { status: "received", received_at: new Date().toISOString().slice(0, 10) });
      toast.success(`Payment #${p.number} marked received`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reconcile = async (p) => {
    try {
      const { data } = await api.post(`/billing/prepay/payments/${p.id}/reconcile`);
      toast.success(`Reconciled to ${data.lines} actual visits (was estimated at ${money(data.prev_estimated)})`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removePayment = async (p) => {
    if (!window.confirm(`Delete payment #${p.number} (${money(p.amount)})? The running balance re-chains.`)) return;
    try { await api.delete(`/billing/prepay/payments/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveLine = async (pid) => {
    setBusy(true);
    try {
      await api.post(`/billing/prepay/payments/${pid}/lines`, { ...line, hours: Number(line.hours) || 0, charge: line.charge === "" ? "" : Number(line.charge) });
      toast.success("Line added");
      setLineFor(null); setLine({ ...emptyLine });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const saveEditLine = async (pid) => {
    setBusy(true);
    try {
      await api.put(`/billing/prepay/payments/${pid}/lines/${editLine.id}`, { ...editLine, hours: Number(editLine.hours) || 0, charge: editLine.charge === "" ? "" : Number(editLine.charge) });
      toast.success("Line updated");
      setEditLine(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const delLine = async (pid, lid) => {
    try { await api.delete(`/billing/prepay/payments/${pid}/lines/${lid}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadBill = async (p) => {
    try {
      const res = await api.get(`/billing/prepay/${clientId}/bill/${p.id}/pdf`, { params: { detail: billDetail }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `prepay_statement_${p.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const copyPortalLink = async () => {
    try {
      let links = (await api.get(`/clients/${clientId}/portal-links`)).data || [];
      if (!links.length) links = [(await api.post(`/clients/${clientId}/portal-links`)).data];
      const url = `${window.location.origin}/portal/${links[0].token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Portal link copied — text it to the payer");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const emailBill = async (p) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/billing/prepay/payments/${p.id}/send`, { detail: billDetail });
      if (data.suppressed) toast.warning(data.message, { duration: 6000 });
      else toast.success(`Statement #${p.number} emailed to ${data.sent_to}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!data) return <div className="mt-6 flex justify-center py-10"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  const carry = data.current_carry || 0;

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="prepay-ledger">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-heading text-lg font-semibold text-stone-900">Payment history — {data.client}</p>
          <p className="mt-0.5 text-xs text-stone-500">
            Rate: {money(data.rate)}/hour{data.prepay.day !== null && data.prepay.day !== undefined ? ` · prepay every ${DAY_NAMES[data.prepay.day]} (arranged ${money(data.prepay.amount)})` : ""}
            {" · "}
            {carry > 0 ? <span className="font-semibold text-[#C0392B]" data-testid="prepay-carry">balance owed: {money(carry)}</span>
              : carry < 0 ? <span className="font-semibold text-[#1e8449]" data-testid="prepay-carry">credit on account: {money(carry)}</span>
              : <span className="text-stone-500" data-testid="prepay-carry">account balanced</span>}
          </p>
          <p className="mt-1 text-[11px] text-stone-400">✓ VERIFIED = completed services confirmed · ⏳ ESTIMATED = projected services (subject to change)</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => window.print()} className="border-stone-300" data-testid="prepay-print"><Printer size={14} className="mr-1" /> Print</Button>
          <Button size="sm" variant="ghost" onClick={onClose} className="text-stone-400" data-testid="prepay-close"><X size={14} /></Button>
        </div>
      </div>

      <div className="mt-4">
        {!addOpen ? (
          <Button size="sm" onClick={() => setAddOpen(true)} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="prepay-add-open">
            <Plus size={14} className="mr-1" /> Add payment #{data.next_number}
          </Button>
        ) : (
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="prepay-add-form">
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">New payment #{data.next_number}</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-5">
              <div><label className="mb-1 block text-[11px] text-stone-500">Amount</label><Input type="number" step="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="h-8 border-stone-300 bg-white text-xs" data-testid="prepay-add-amount" /></div>
              <div><label className="mb-1 block text-[11px] text-stone-500">Due date</label><Input type="date" value={pay.due_date} onChange={(e) => setPay({ ...pay, due_date: e.target.value })} className="h-8 border-stone-300 bg-white text-xs" data-testid="prepay-add-due" /></div>
              <div><label className="mb-1 block text-[11px] text-stone-500">Services from</label><Input type="date" value={pay.services_start} onChange={(e) => setPay({ ...pay, services_start: e.target.value })} className="h-8 border-stone-300 bg-white text-xs" data-testid="prepay-add-start" /></div>
              <div><label className="mb-1 block text-[11px] text-stone-500">Services through</label><Input type="date" value={pay.services_end} onChange={(e) => setPay({ ...pay, services_end: e.target.value })} className="h-8 border-stone-300 bg-white text-xs" data-testid="prepay-add-end" /></div>
              <div><label className="mb-1 block text-[11px] text-stone-500">Note (optional)</label><Input value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} className="h-8 border-stone-300 bg-white text-xs" data-testid="prepay-add-note" /></div>
            </div>
            <label className="mt-3 flex items-center gap-2 text-xs text-stone-600">
              <input type="checkbox" checked={pay.autofill} onChange={(e) => setPay({ ...pay, autofill: e.target.checked })} data-testid="prepay-add-autofill" />
              Project the shift lines from this client's schedule (TBD where no caregiver is assigned yet)
            </label>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={addPayment} disabled={busy || !Number(pay.amount)} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="prepay-add-submit">{busy ? "Adding…" : "Add payment"}</Button>
              <Button size="sm" variant="ghost" onClick={() => setAddOpen(false)} className="text-stone-400" data-testid="prepay-add-cancel">Cancel</Button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 space-y-4">
        {(data.payments || []).length === 0 && (
          <p className="rounded-lg border border-dashed border-stone-300 p-6 text-center text-xs text-stone-400" data-testid="prepay-empty">No payments yet — add the first prepay above.</p>
        )}
        {(data.payments || []).map((p) => (
          <div key={p.id} className="rounded-xl border border-stone-200 p-5" data-testid={`prepay-payment-${p.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-stone-900">
                  PAYMENT #{p.number}: {money(p.amount)} — {fmtD(p.received_at || p.due_date)}
                </p>
                <p className="mt-0.5 text-xs text-stone-500">
                  Services: {fmtD(p.services_start)} – {fmtD(p.services_end)} {p.status === "prepay" ? "· ⏳ PROJECTED" : "· ✓ COMPLETED"}
                </p>
              </div>
              <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase ${(STATUS[p.status] || STATUS.prepay).cls}`} data-testid={`prepay-status-${p.id}`}>
                {(STATUS[p.status] || STATUS.prepay).label}
              </span>
            </div>

            {(p.lines || []).length > 0 && (
              <div className="mt-3 overflow-x-auto rounded-lg border border-stone-100">
                <table className="w-full text-xs">
                  <thead><tr className="bg-stone-50 text-left text-stone-400">
                    <th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5">Caregiver</th><th className="px-2 py-1.5">Time</th>
                    <th className="px-2 py-1.5 text-right">Hours</th><th className="px-2 py-1.5 text-right">Charge</th><th className="px-2 py-1.5"></th>
                  </tr></thead>
                  <tbody>
                    {p.lines.map((l) => (
                      editLine?.id === l.id ? (
                        <tr key={l.id} className="border-t border-stone-100 bg-primary/5">
                          <td className="px-2 py-1"><Input type="date" value={editLine.date} onChange={(e) => setEditLine({ ...editLine, date: e.target.value })} className="h-7 w-32 border-stone-300 text-xs" data-testid="prepay-line-edit-date" /></td>
                          <td className="px-2 py-1"><Input value={editLine.caregiver} onChange={(e) => setEditLine({ ...editLine, caregiver: e.target.value })} className="h-7 w-28 border-stone-300 text-xs" data-testid="prepay-line-edit-caregiver" /></td>
                          <td className="px-2 py-1"><Input value={editLine.time} onChange={(e) => setEditLine({ ...editLine, time: e.target.value })} className="h-7 w-24 border-stone-300 text-xs" data-testid="prepay-line-edit-time" /></td>
                          <td className="px-2 py-1 text-right"><Input type="number" step="0.25" value={editLine.hours} onChange={(e) => setEditLine({ ...editLine, hours: e.target.value })} className="h-7 w-16 border-stone-300 text-xs" data-testid="prepay-line-edit-hours" /></td>
                          <td className="px-2 py-1 text-right"><Input type="number" step="0.01" value={editLine.charge} onChange={(e) => setEditLine({ ...editLine, charge: e.target.value })} className="h-7 w-20 border-stone-300 text-xs" data-testid="prepay-line-edit-charge" /></td>
                          <td className="px-2 py-1 text-right whitespace-nowrap">
                            <button onClick={() => saveEditLine(p.id)} className="text-[#1e8449]" data-testid="prepay-line-edit-save"><Check size={14} /></button>
                            <button onClick={() => setEditLine(null)} className="ml-2 text-stone-400" data-testid="prepay-line-edit-cancel"><X size={14} /></button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={l.id} className="border-t border-stone-100" data-testid={`prepay-line-${l.id}`}>
                          <td className="px-2 py-1">{fmtD(l.date)}{l.estimated ? <span className="ml-1 text-stone-300">⏳</span> : ""}</td>
                          <td className="px-2 py-1">{l.caregiver}{l.overnight ? " (overnight)" : ""}{l.holiday ? " (HOLIDAY 1.5x)" : ""}</td>
                          <td className="px-2 py-1">{l.time}</td>
                          <td className="px-2 py-1 text-right">{Number(l.hours).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right">{money(l.charge)}</td>
                          <td className="px-2 py-1 text-right whitespace-nowrap">
                            <button onClick={() => setEditLine({ ...l })} className="text-stone-400 hover:text-primary" data-testid={`prepay-line-edit-${l.id}`}><PencilSimple size={13} /></button>
                            <button onClick={() => delLine(p.id, l.id)} className="ml-2 text-stone-300 hover:text-[#C0392B]" data-testid={`prepay-line-del-${l.id}`}><Trash size={13} /></button>
                          </td>
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-3 space-y-0.5 text-right text-xs" data-testid={`prepay-totals-${p.id}`}>
              <p className="text-stone-600">Services Total: <span className="font-semibold text-stone-900">{money(p.services_total)}</span></p>
              <p className="text-stone-500">{p.carry_in > 0 ? `Balance from #${p.number - 1}` : `Credit from #${p.number - 1}`}: {money(p.carry_in || 0)}</p>
              <p className="text-stone-600">{p.status === "prepay" ? "Payment Projected" : "Payment Applied"}: <span className="font-semibold text-stone-900">{money(p.amount)}</span></p>
              <p className={p.carry_out > 0 ? "font-semibold text-[#C0392B]" : p.carry_out < 0 ? "font-semibold text-[#1e8449]" : "text-stone-500"}>
                {p.carry_out > 0 ? `Balance to #${p.number + 1}` : p.carry_out < 0 ? "Credit to next" : "Balances to"}: {money(p.carry_out || 0)}
              </p>
              {p.prev_estimated != null && p.prev_estimated !== p.services_total && (
                <p className="text-[11px] italic text-stone-400">Previously estimated {money(p.prev_estimated)} — actual is {money(p.services_total)}</p>
              )}
              {p.note && <p className="text-[11px] italic text-stone-400">{p.note}</p>}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-stone-100 pt-3">
              {p.status === "prepay" && (
                <Button size="sm" variant="outline" onClick={() => markReceived(p)} className="rounded-full border-[#27AE60]/40 text-[#1e8449] hover:bg-[#27AE60] hover:text-white" data-testid={`prepay-receive-${p.id}`}>Mark received</Button>
              )}
              {p.status !== "verified" && (
                <Button size="sm" variant="outline" onClick={() => reconcile(p)} className="rounded-full border-primary/40 text-primary hover:bg-primary hover:text-white" data-testid={`prepay-reconcile-${p.id}`}>Reconcile to actual visits</Button>
              )}
              <Button size="sm" variant="outline" onClick={() => { setLineFor(lineFor === p.id ? null : p.id); setLine({ ...emptyLine }); }} className="rounded-full border-stone-300" data-testid={`prepay-addline-${p.id}`}>
                <Plus size={13} className="mr-1" /> Line
              </Button>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 px-2.5 py-1" data-testid={`prepay-bill-tools-${p.id}`}>
                <label className="flex items-center gap-1 text-[10px] font-semibold text-stone-500" title="Include projected shift lines on the statement">
                  <input type="checkbox" checked={billDetail} onChange={(e) => setBillDetail(e.target.checked)} data-testid={`prepay-bill-detail-${p.id}`} />
                  shift detail
                </label>
                <button onClick={() => downloadBill(p)} className="text-[11px] font-semibold text-primary hover:underline" data-testid={`prepay-bill-pdf-${p.id}`}>Download bill PDF</button>
                <button onClick={copyPortalLink} className="text-[11px] font-semibold text-primary hover:underline" data-testid={`prepay-bill-link-${p.id}`}>Copy portal link</button>
                <button onClick={() => emailBill(p)} disabled={busy} className="text-[11px] font-semibold text-primary hover:underline" data-testid={`prepay-bill-email-${p.id}`}>Email to payer</button>
              </span>
              {p.emailed_at && <span className="text-[10px] text-stone-400" data-testid={`prepay-bill-emailed-${p.id}`}>emailed {String(p.emailed_at).slice(0, 10)} to {p.emailed_to}</span>}
              <Button size="sm" variant="ghost" onClick={() => removePayment(p)} className="ml-auto text-stone-300 hover:text-[#C0392B]" data-testid={`prepay-delete-${p.id}`}><Trash size={13} /></Button>
            </div>
            {lineFor === p.id && (
              <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg bg-stone-50 p-3" data-testid={`prepay-lineform-${p.id}`}>
                <div><label className="mb-1 block text-[11px] text-stone-500">Date</label><Input type="date" value={line.date} onChange={(e) => setLine({ ...line, date: e.target.value })} className="h-8 w-36 border-stone-300 bg-white text-xs" data-testid="prepay-line-date" /></div>
                <div><label className="mb-1 block text-[11px] text-stone-500">Caregiver</label><Input value={line.caregiver} onChange={(e) => setLine({ ...line, caregiver: e.target.value })} placeholder="TBD" className="h-8 w-32 border-stone-300 bg-white text-xs" data-testid="prepay-line-caregiver" /></div>
                <div><label className="mb-1 block text-[11px] text-stone-500">Time</label><Input value={line.time} onChange={(e) => setLine({ ...line, time: e.target.value })} placeholder="4pm-9pm" className="h-8 w-24 border-stone-300 bg-white text-xs" data-testid="prepay-line-time" /></div>
                <div><label className="mb-1 block text-[11px] text-stone-500">Hours</label><Input type="number" step="0.25" value={line.hours} onChange={(e) => setLine({ ...line, hours: e.target.value })} className="h-8 w-20 border-stone-300 bg-white text-xs" data-testid="prepay-line-hours" /></div>
                <div><label className="mb-1 block text-[11px] text-stone-500">Charge (auto if blank)</label><Input type="number" step="0.01" value={line.charge} onChange={(e) => setLine({ ...line, charge: e.target.value })} className="h-8 w-24 border-stone-300 bg-white text-xs" data-testid="prepay-line-charge" /></div>
                <label className="flex items-center gap-1 text-[11px] text-stone-500"><input type="checkbox" checked={line.overnight} onChange={(e) => setLine({ ...line, overnight: e.target.checked })} data-testid="prepay-line-overnight" /> overnight</label>
                <label className="flex items-center gap-1 text-[11px] text-stone-500"><input type="checkbox" checked={line.holiday} onChange={(e) => setLine({ ...line, holiday: e.target.checked })} data-testid="prepay-line-holiday" /> holiday 1.5x</label>
                <Button size="sm" onClick={() => saveLine(p.id)} disabled={busy || !line.date || !Number(line.hours)} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="prepay-line-save">Add line</Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {data.prepay.note && (
        <div className="mt-5 rounded-xl bg-stone-50 p-5 text-xs leading-relaxed text-stone-600" data-testid="prepay-client-note">
          <p className="font-semibold text-stone-800">Client note</p>
          <p className="mt-1.5">{data.prepay.note}</p>
        </div>
      )}
    </div>
  );
}
